import { useCallback, useRef, useState, type MouseEvent } from "react";

export type RailRowMenuPoint = { x: number; y: number };

export type RailRowMenuController = {
  /** Whether the menu is open. */
  open: boolean;
  /** Controlled open setter (wire to the menu's `onOpenChange`). */
  setOpen: (open: boolean) => void;
  /** Viewport coords the menu anchors to (cursor on right-click, kebab otherwise). */
  point: RailRowMenuPoint;
  /** Row right-click → open the menu at the cursor. Spread on the row container. */
  onContextMenu: (e: MouseEvent) => void;
  /** Kebab click → toggle the menu, anchored just below the button. */
  toggleFromButton: (e: MouseEvent) => void;
};

/**
 * Standard wiring for a rail row's three-dot menu, shared by every rail via
 * [`RailRowMenu`]. The menu anchors to a movable point so it opens **where the
 * user right-clicked** (matching the canvas rail), while a kebab click still
 * drops the menu from the button.
 *
 * The kebab's pointerdown is stopped (in `RailRowMenu`) so Radix's
 * dismiss-on-outside doesn't fire for the trigger — that keeps the kebab a clean
 * toggle even though the actual Radix trigger is a virtual element at `point`.
 */
export function useRailRowContextMenu(): RailRowMenuController {
  const [open, setOpenState] = useState(false);
  const openRef = useRef(false);
  const [point, setPoint] = useState<RailRowMenuPoint>({ x: 0, y: 0 });

  const setOpen = useCallback((next: boolean) => {
    openRef.current = next;
    setOpenState(next);
  }, []);

  const onContextMenu = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setPoint({ x: e.clientX, y: e.clientY });
      setOpen(true);
    },
    [setOpen],
  );

  const toggleFromButton = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (openRef.current) {
        setOpen(false);
        return;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      setPoint({ x: rect.left, y: rect.bottom });
      setOpen(true);
    },
    [setOpen],
  );

  return { open, setOpen, point, onContextMenu, toggleFromButton };
}
